import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Toast } from '../components/common';
import ejecutivoService from '../services/ejecutivo.service';
import statementService from '../services/statement.service';
import { ClienteEjecutivo, EjecutivoStats, DashboardData, DashboardFacturaPendiente, DashboardPagoReciente, DashboardTopProducto, DashboardTopMarca, InvoiceLine } from '../types';
import './EjecutivoPanel.css';

type PeriodoTipo = 'mes_actual' | 'mes_anterior';

const EjecutivoPanel: React.FC = () => {
  // Estados para lista de clientes
  const [clientes, setClientes] = useState<ClienteEjecutivo[]>([]);
  const [stats, setStats] = useState<EjecutivoStats | null>(null);
  const [isLoadingClientes, setIsLoadingClientes] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClientes, setTotalClientes] = useState(0);

  // Estados para ver dashboard de cliente
  const [selectedCliente, setSelectedCliente] = useState<ClienteEjecutivo | null>(null);
  const [clienteDashboard, setClienteDashboard] = useState<DashboardData | null>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [periodoTipo, setPeriodoTipo] = useState<PeriodoTipo>('mes_actual');

  // Modal de detalle de factura
  const [selectedInvoice, setSelectedInvoice] = useState<DashboardFacturaPendiente | null>(null);
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);

  // Modal de resetear contraseña
  const [showResetModal, setShowResetModal] = useState(false);
  const [clienteToReset, setClienteToReset] = useState<ClienteEjecutivo | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Modal de enviar por correo
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Estado para descargar PDF
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Toast
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  // Cargar estadísticas
  const loadStats = useCallback(async () => {
    try {
      const data = await ejecutivoService.getMisEstadisticas();
      setStats(data);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  }, []);

  // Cargar clientes
  const loadClientes = useCallback(async (page = 1, search = '') => {
    try {
      setIsLoadingClientes(true);
      const result = await ejecutivoService.getMisClientes({
        page,
        limit: 10,
        search: search || undefined
      });
      setClientes(result.clientes);
      setCurrentPage(result.pagination.page);
      setTotalPages(result.pagination.totalPages);
      setTotalClientes(result.pagination.total);
    } catch (err: any) {
      setToast({
        isVisible: true,
        message: err.message || 'Error al cargar clientes',
        type: 'error'
      });
    } finally {
      setIsLoadingClientes(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadClientes();
  }, [loadStats, loadClientes]);

  // Búsqueda con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadClientes(1, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, loadClientes]);

  // Calcular fechas según periodo
  const calcularFechas = useCallback((tipo: PeriodoTipo) => {
    const now = new Date();
    let startDate = '';
    let endDate = '';

    switch (tipo) {
      case 'mes_actual':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
        break;
      case 'mes_anterior':
        const primerDiaMesAnterior = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const ultimoDiaMesAnterior = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = primerDiaMesAnterior.toISOString().split('T')[0];
        endDate = ultimoDiaMesAnterior.toISOString().split('T')[0];
        break;
    }

    return { startDate, endDate };
  }, []);

  // Cargar dashboard de cliente
  const loadClienteDashboard = useCallback(async (partnerId: number, tipo?: PeriodoTipo) => {
    try {
      setIsLoadingDashboard(true);
      const periodoActual = tipo || periodoTipo;
      const { startDate, endDate } = calcularFechas(periodoActual);

      const data = await ejecutivoService.getClienteDashboard(partnerId, {
        startDate,
        endDate
      });
      setClienteDashboard(data);
    } catch (err: any) {
      setToast({
        isVisible: true,
        message: err.message || 'Error al cargar dashboard',
        type: 'error'
      });
    } finally {
      setIsLoadingDashboard(false);
    }
  }, [periodoTipo, calcularFechas]);

  // Manejar selección de cliente
  const handleSelectCliente = (cliente: ClienteEjecutivo) => {
    setSelectedCliente(cliente);
    loadClienteDashboard(cliente.id);
  };

  // Volver a lista de clientes
  const handleBackToList = () => {
    setSelectedCliente(null);
    setClienteDashboard(null);
  };

  // Manejar cambio de periodo
  const handlePeriodoChange = (tipo: PeriodoTipo) => {
    setPeriodoTipo(tipo);
    if (selectedCliente) {
      loadClienteDashboard(selectedCliente.id, tipo);
    }
  };

  // Formatters
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getEstadoClass = (estado: string): string => {
    switch (estado) {
      case 'paid': return 'estado-pagado';
      case 'partial': return 'estado-parcial';
      case 'not_paid':
      default: return 'estado-pendiente';
    }
  };

  const getEstadoLabel = (estado: string): string => {
    switch (estado) {
      case 'paid': return 'Pagada';
      case 'partial': return 'Parcial';
      case 'not_paid':
      default: return 'Pendiente';
    }
  };

  const getNombrePeriodo = (): string => {
    const now = new Date();
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    switch (periodoTipo) {
      case 'mes_actual':
        return `1 - ${now.getDate()} ${meses[now.getMonth()]} ${now.getFullYear()}`;
      case 'mes_anterior':
        const mesAnteriorIndex = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const añoMesAnterior = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const ultimoDia = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        return `1 - ${ultimoDia} ${meses[mesAnteriorIndex]} ${añoMesAnterior}`;
      default:
        return '';
    }
  };

  // Ver detalle de factura
  const handleVerDetalle = async (factura: DashboardFacturaPendiente) => {
    setSelectedInvoice(factura);
    setLoadingLines(true);
    try {
      const result = await statementService.getInvoiceDetail(factura.id);
      setInvoiceLines(result.lines);
    } catch (err) {
      console.error('Error cargando detalle de factura:', err);
      setInvoiceLines([]);
    } finally {
      setLoadingLines(false);
    }
  };

  const closeInvoiceModal = () => {
    setSelectedInvoice(null);
    setInvoiceLines([]);
  };

  // Resetear contraseña de cliente
  const handleOpenResetModal = (cliente: ClienteEjecutivo, e: React.MouseEvent) => {
    e.stopPropagation();
    setClienteToReset(cliente);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleResetPassword = async () => {
    if (!clienteToReset) return;

    try {
      setIsResetting(true);
      const result = await ejecutivoService.resetClientePassword(
        clienteToReset.id,
        newPassword || undefined
      );

      setToast({
        isVisible: true,
        message: `Contraseña restablecida. ${result.tempPassword ? `Nueva contraseña: ${result.tempPassword}` : ''}`,
        type: 'success'
      });
      setShowResetModal(false);
    } catch (err: any) {
      setToast({
        isVisible: true,
        message: err.message || 'Error al restablecer contraseña',
        type: 'error'
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Descargar PDF del estado de cuenta del cliente
  const handleDownloadPDF = async () => {
    if (!selectedCliente) return;

    try {
      setIsDownloadingPDF(true);
      const { startDate, endDate } = calcularFechas(periodoTipo);

      await ejecutivoService.downloadClientePDF(selectedCliente.id, {
        startDate,
        endDate
      });

      setToast({
        isVisible: true,
        message: 'PDF descargado exitosamente',
        type: 'success'
      });
    } catch (err: any) {
      setToast({
        isVisible: true,
        message: err.message || 'Error al descargar PDF',
        type: 'error'
      });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Abrir modal de enviar por correo
  const handleOpenEmailModal = () => {
    if (clienteDashboard?.cliente?.email) {
      setEmailToSend(clienteDashboard.cliente.email);
    } else {
      setEmailToSend('');
    }
    setShowEmailModal(true);
  };

  // Enviar estado de cuenta por correo
  const handleSendEmail = async () => {
    if (!selectedCliente) return;

    try {
      setIsSendingEmail(true);
      const { startDate, endDate } = calcularFechas(periodoTipo);

      const result = await ejecutivoService.sendClienteStatementByEmail(selectedCliente.id, {
        email: emailToSend || undefined,
        startDate,
        endDate
      });

      setToast({
        isVisible: true,
        message: result.message || `Estado de cuenta enviado a ${result.sentTo}`,
        type: 'success'
      });
      setShowEmailModal(false);
    } catch (err: any) {
      setToast({
        isVisible: true,
        message: err.message || 'Error al enviar por correo',
        type: 'error'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Renderizar lista de clientes
  const renderClientesList = () => (
    <div className="ejecutivo-panel">
      {/* Stats Cards */}
      <div className="ejecutivo-stats">
        <div className="stat-card">
          <div className="stat-icon stat-icon-blue">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Mis Clientes</span>
            <span className="stat-value">{stats?.totalClientes || 0}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <polyline points="17 11 19 13 23 9" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Con Cuenta Activa</span>
            <span className="stat-value">{stats?.clientesConCuenta || 0}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-red">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Saldo Pendiente Total</span>
            <span className="stat-value text-error">{formatCurrency(stats?.saldoPendienteTotal || 0)}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-purple">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Ventas Historicas</span>
            <span className="stat-value">{formatCurrency(stats?.ventasHistoricas || 0)}</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="clientes-header">
        <h2>Mis Clientes</h2>
        <div className="search-box">
          <Input
            type="text"
            placeholder="Buscar cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Clientes Table */}
      {isLoadingClientes ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando clientes...</p>
        </div>
      ) : clientes.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
          <p>No se encontraron clientes</p>
        </div>
      ) : (
        <>
          <div className="clientes-table-container">
            <table className="clientes-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Ciudad</th>
                  <th>Saldo Pendiente</th>
                  <th>Ventas Históricas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((cliente) => (
                  <tr key={cliente.id} onClick={() => handleSelectCliente(cliente)} className="clickable-row">
                    <td className="cliente-name">
                      <div className="cliente-avatar">
                        {cliente.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="cliente-info">
                        <span className="name">{cliente.name}</span>
                        {cliente.vat && <span className="vat">{cliente.vat}</span>}
                      </div>
                    </td>
                    <td>{cliente.city || '-'}</td>
                    <td className="amount-cell text-error">{formatCurrency(cliente.credit)}</td>
                    <td className="amount-cell">{formatCurrency(cliente.totalInvoiced)}</td>
                    <td className="actions-cell">
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() => handleSelectCliente(cliente)}
                      >
                        Ver Dashboard
                      </Button>
                      {cliente.hasUser && (
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={(e) => handleOpenResetModal(cliente, e)}
                        >
                          Reset Pass
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <span className="pagination-info">
              Mostrando {clientes.length} de {totalClientes} clientes
            </span>
            <div className="pagination-buttons">
              <Button
                variant="secondary"
                size="small"
                disabled={currentPage === 1}
                onClick={() => loadClientes(currentPage - 1, searchTerm)}
              >
                Anterior
              </Button>
              <span className="page-indicator">
                Pagina {currentPage} de {totalPages}
              </span>
              <Button
                variant="secondary"
                size="small"
                disabled={currentPage === totalPages}
                onClick={() => loadClientes(currentPage + 1, searchTerm)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Renderizar dashboard de cliente
  const renderClienteDashboard = () => (
    <div className="cliente-dashboard-view">
      {/* Header con botón de volver y acciones */}
      <div className="dashboard-header-actions">
        <button className="back-button" onClick={handleBackToList}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Volver a mis clientes
        </button>

        <div className="dashboard-actions">
          <Button
            variant="secondary"
            onClick={handleDownloadPDF}
            isLoading={isDownloadingPDF}
            disabled={isLoadingDashboard}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Descargar PDF
          </Button>
          <Button
            variant="primary"
            onClick={handleOpenEmailModal}
            disabled={isLoadingDashboard}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Enviar por Correo
          </Button>
        </div>
      </div>

      {isLoadingDashboard ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      ) : clienteDashboard ? (
        <div className="dashboard">
          {/* Client Info Header con Resumen Financiero Integrado */}
          <div className="client-profile-card">
            {/* Parte superior: Datos del cliente */}
            <div className="client-profile-header">
              <div className="client-avatar-section">
                <div className="client-avatar-lg">
                  {clienteDashboard.cliente.nombre.charAt(0).toUpperCase()}
                </div>
                <div className="client-status-indicator"></div>
              </div>
              <div className="client-details">
                <h2 className="client-name-lg">{clienteDashboard.cliente.nombre}</h2>
                <div className="client-contact-info">
                  <span className="contact-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {clienteDashboard.cliente.email || 'No disponible'}
                  </span>
                  {clienteDashboard.cliente.telefono && (
                    <span className="contact-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      {clienteDashboard.cliente.telefono}
                    </span>
                  )}
                  {clienteDashboard.cliente.ejecutivo && (
                    <span className="contact-item ejecutivo">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      {clienteDashboard.cliente.ejecutivo}
                    </span>
                  )}
                </div>
              </div>
              {clienteDashboard.cliente.rfc && typeof clienteDashboard.cliente.rfc === 'string' && (
                <div className="client-rfc-badge">
                  <span className="rfc-label">RFC</span>
                  <span className="rfc-value">{clienteDashboard.cliente.rfc}</span>
                </div>
              )}
            </div>

            {/* Separador visual */}
            <div className="profile-divider"></div>

            {/* Parte inferior: Resumen financiero */}
            <div className="financial-summary">
              <div className="financial-item saldo-pendiente">
                <div className="financial-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div className="financial-content">
                  <span className="financial-label">Saldo Pendiente</span>
                  <span className="financial-value danger">{formatCurrency(clienteDashboard.resumen.saldoPendiente || 0)}</span>
                </div>
              </div>

              <div className="financial-item linea-credito">
                <div className="financial-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <div className="financial-content">
                  <span className="financial-label">Línea de Crédito</span>
                  <span className="financial-value">{formatCurrency(clienteDashboard.resumen.lineaCredito || 0)}</span>
                </div>
              </div>

              <div className="financial-item credito-disponible">
                <div className="financial-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <div className="financial-content">
                  <span className="financial-label">Crédito Disponible</span>
                  <span className="financial-value success">{formatCurrency(clienteDashboard.resumen.creditoDisponible || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Period Selector */}
          <div className="period-selector">
            <div className="period-buttons">
              <button
                className={`period-btn ${periodoTipo === 'mes_actual' ? 'active' : ''}`}
                onClick={() => handlePeriodoChange('mes_actual')}
              >
                Mes Actual
              </button>
              <button
                className={`period-btn ${periodoTipo === 'mes_anterior' ? 'active' : ''}`}
                onClick={() => handlePeriodoChange('mes_anterior')}
              >
                Mes Anterior
              </button>
            </div>
            <span className="period-label">{getNombrePeriodo()}</span>
          </div>

          {/* Stats Cards - Solo Compras y Pagado del Periodo */}
          <div className="stats-grid stats-grid-2">
            <div className="stat-card">
              <div className="stat-icon stat-icon-blue">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-label">Compras del Periodo</span>
                <span className="stat-value">{formatCurrency(clienteDashboard.resumen.totalCompras || 0)}</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-label">Pagado del Periodo</span>
                <span className="stat-value text-success">{formatCurrency(clienteDashboard.resumen.totalPagado || 0)}</span>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="dashboard-content">
            {/* Pending Invoices Table */}
            <div className="dashboard-card invoices-card">
              <div className="card-header">
                <h3 className="card-title">Facturas Pendientes</h3>
              </div>

              {clienteDashboard.facturasPendientes && clienteDashboard.facturasPendientes.length > 0 ? (
                <div className="table-container">
                  <table className="invoices-table">
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <th>Fecha</th>
                        <th>Vencimiento</th>
                        <th>Total</th>
                        <th>Pendiente</th>
                        <th>Estado</th>
                        <th>Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clienteDashboard.facturasPendientes.slice(0, 5).map((factura: DashboardFacturaPendiente) => (
                        <tr key={factura.id}>
                          <td className="folio-cell">{factura.folio}</td>
                          <td>{formatDate(factura.fecha)}</td>
                          <td>{formatDate(factura.fechaVencimiento)}</td>
                          <td className="amount-cell">{formatCurrency(factura.total)}</td>
                          <td className="amount-cell text-error">{formatCurrency(factura.saldoPendiente)}</td>
                          <td>
                            <span className={`estado-badge ${getEstadoClass(factura.estado)}`}>
                              {getEstadoLabel(factura.estado)}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-ver-detalle"
                              onClick={() => handleVerDetalle(factura)}
                            >
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <p>No hay facturas pendientes</p>
                </div>
              )}
            </div>

            {/* Recent Payments */}
            <div className="dashboard-card payments-card">
              <div className="card-header">
                <h3 className="card-title">
                  Pagos y Abonos del Periodo
                  {clienteDashboard.pagosRecientes && clienteDashboard.pagosRecientes.length > 0 && (
                    <span className="payments-count">{clienteDashboard.pagosRecientes.length}</span>
                  )}
                </h3>
              </div>

              {clienteDashboard.pagosRecientes && clienteDashboard.pagosRecientes.length > 0 ? (
                <div className="payments-list-wrapper">
                  <div className="payments-list">
                    {clienteDashboard.pagosRecientes.map((pago: DashboardPagoReciente) => (
                      <div key={pago.id} className="payment-item">
                        <div className="payment-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                            <path d="M16 12l-4-4-4 4M12 16V8" />
                          </svg>
                        </div>
                        <div className="payment-info">
                          <span className="payment-ref">{pago.referencia}</span>
                          <span className="payment-date">{formatDate(pago.fecha)}</span>
                        </div>
                        <div className="payment-amount">
                          <span className="amount text-success">+{formatCurrency(pago.monto)}</span>
                          <span className="payment-type">{pago.tipo}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  <p>No hay pagos ni abonos en este periodo</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Products & Brands */}
          <div className="charts-grid">
            {clienteDashboard.topProductos && clienteDashboard.topProductos.length > 0 && (
              <div className="dashboard-card top-products-chart">
                <div className="chart-header">
                  <div className="chart-title-section">
                    <div className="chart-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 20V10M12 20V4M6 20v-6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="chart-title">Top 5 Productos</h3>
                      <p className="chart-subtitle">Productos mas comprados (ultimos 3 meses)</p>
                    </div>
                  </div>
                </div>

                <div className="horizontal-bar-chart">
                  {(() => {
                    const topProductos = clienteDashboard.topProductos || [];
                    const maxCantidad = topProductos[0]?.cantidadTotal || 1;

                    return topProductos.slice(0, 5).map((producto: DashboardTopProducto, index: number) => {
                      const porcentaje = (producto.cantidadTotal / maxCantidad) * 100;
                      const getRankClass = () => {
                        if (index === 0) return 'gold';
                        if (index === 1) return 'silver';
                        if (index === 2) return 'bronze';
                        return 'default';
                      };

                      return (
                        <div key={producto.id} className={`chart-bar-row ${getRankClass()}`}>
                          <div className="bar-label">
                            <span className="bar-rank">#{index + 1}</span>
                            <span className="bar-product-name" title={producto.nombre}>
                              {producto.nombre}
                            </span>
                          </div>
                          <div className="bar-track">
                            <div className={`bar-fill ${getRankClass()}`} style={{ width: `${porcentaje}%` }}>
                              <span className="bar-value">{Math.round(producto.cantidadTotal)}</span>
                            </div>
                          </div>
                          <div className="bar-stats">
                            <span className="stat-orders">{producto.vecesComprado} compras</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {clienteDashboard.topMarcas && clienteDashboard.topMarcas.length > 0 && (
              <div className="dashboard-card top-brands-chart">
                <div className="chart-header">
                  <div className="chart-title-section">
                    <div className="chart-icon brand-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                        <line x1="7" y1="7" x2="7.01" y2="7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="chart-title">Top 5 Marcas</h3>
                      <p className="chart-subtitle">Marcas mas compradas (ultimos 3 meses)</p>
                    </div>
                  </div>
                </div>

                <div className="horizontal-bar-chart">
                  {(() => {
                    const topMarcas = clienteDashboard.topMarcas || [];
                    const maxCantidad = topMarcas[0]?.cantidadTotal || 1;

                    return topMarcas.slice(0, 5).map((marca: DashboardTopMarca, index: number) => {
                      const porcentaje = (marca.cantidadTotal / maxCantidad) * 100;
                      const getRankClass = () => {
                        if (index === 0) return 'gold';
                        if (index === 1) return 'silver';
                        if (index === 2) return 'bronze';
                        return 'default';
                      };

                      return (
                        <div key={marca.id || index} className={`chart-bar-row brand-row ${getRankClass()}`}>
                          <div className="bar-label">
                            <span className="bar-rank">#{index + 1}</span>
                            <span className="bar-product-name" title={marca.nombre}>
                              {marca.nombre}
                            </span>
                          </div>
                          <div className="bar-track">
                            <div className={`bar-fill brand-fill ${getRankClass()}`} style={{ width: `${porcentaje}%` }}>
                              <span className="bar-value">{Math.round(marca.cantidadTotal)}</span>
                            </div>
                          </div>
                          <div className="bar-stats">
                            <span className="stat-orders">{marca.productosUnicos} productos</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="ejecutivo-panel-container">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      {selectedCliente ? renderClienteDashboard() : renderClientesList()}

      {/* Modal Detalle de Factura */}
      {selectedInvoice && (
        <div className="modal-overlay" onClick={closeInvoiceModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalle de Factura</h3>
              <button className="modal-close" onClick={closeInvoiceModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-invoice-info">
              <div className="invoice-info-row">
                <span className="info-key">Folio:</span>
                <span className="info-value">{selectedInvoice.folio}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Fecha:</span>
                <span className="info-value">{formatDate(selectedInvoice.fecha)}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Vencimiento:</span>
                <span className="info-value">{formatDate(selectedInvoice.fechaVencimiento)}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Total:</span>
                <span className="info-value">{formatCurrency(selectedInvoice.total)}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Saldo Pendiente:</span>
                <span className="info-value text-error">{formatCurrency(selectedInvoice.saldoPendiente)}</span>
              </div>
            </div>

            <div className="modal-lines-section">
              <h4>Productos / Servicios</h4>
              {loadingLines ? (
                <div className="loading-lines">
                  <div className="spinner"></div>
                  <span>Cargando detalle...</span>
                </div>
              ) : invoiceLines.length > 0 ? (
                <div className="lines-table-container">
                  <table className="lines-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceLines.map((line) => (
                        <tr key={line.id}>
                          <td className="product-name">
                            {Array.isArray(line.product_id) ? line.product_id[1] : line.name}
                          </td>
                          <td className="qty-cell">{line.quantity}</td>
                          <td className="price-cell">{formatCurrency(line.price_unit)}</td>
                          <td className="subtotal-cell">{formatCurrency(line.price_subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-lines">
                  <p>No se encontraron productos en esta factura</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={closeInvoiceModal}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reset Password */}
      {showResetModal && clienteToReset && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Restablecer Contrasena</h3>
              <button className="modal-close" onClick={() => setShowResetModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p>Restablecer contrasena para: <strong>{clienteToReset.name}</strong></p>
              <Input
                label="Nueva contrasena (dejar vacio para generar temporal)"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Temporal123!"
              />
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowResetModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleResetPassword} isLoading={isResetting}>
                Restablecer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Enviar por Correo */}
      {showEmailModal && selectedCliente && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enviar Estado de Cuenta</h3>
              <button className="modal-close" onClick={() => setShowEmailModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p>Enviar estado de cuenta de: <strong>{selectedCliente.name}</strong></p>
              <p className="modal-period-info">Periodo: {getNombrePeriodo()}</p>
              <Input
                label="Correo electronico del destinatario"
                type="email"
                value={emailToSend}
                onChange={(e) => setEmailToSend(e.target.value)}
                placeholder="cliente@ejemplo.com"
              />
              <p className="form-hint">Dejar vacio para usar el correo registrado del cliente</p>
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleSendEmail} isLoading={isSendingEmail}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                Enviar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EjecutivoPanel;
